import {
  actionColumn,
  Button,
  CollapseToggle,
  column,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridRowLookup,
  GridTableLayout,
  numericColumn,
  PageHeaderLayout,
  RowStyles,
  simpleHeader,
  singleFilter,
  useGridTableLayoutState,
  useSnackbar,
  useSuperDrawer,
} from "@homebound/beam";
import { MutableRefObject, useMemo, useRef } from "react";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import {
  AdminCostCodeFragment,
  AdminCostDivisionFragment,
  CostCodesQuery,
  useCostCodesQuery,
  useDeleteCostCodeMutation,
} from "src/routes/admin/costCodes/CostCodes.generated";
import { groupBy } from "src/utils";
import { CostCodesSuperDrawerTabs } from "./CostCodesSuperDrawerTabs";
import { CreateNewCostCodeModal } from "./CreateNewCostCodeModal";

/** Lists all cost divisions/codes in the system. */
export function CostCodesPage() {
  const rowLookup = useRef<GridRowLookup<Row>>();
  const { openInDrawer } = useSuperDrawer();
  const filterDefs = useMemo(() => {
    return {
      version: singleFilter({
        options: [
          { id: 1, name: "1" },
          { id: 2, name: "2" },
        ],
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
        defaultValue: 1,
      }),
    };
  }, []);

  const layoutState = useGridTableLayoutState<LocalCostCodeFilter>({
    persistedFilter: { storageKey: "itemsFilter", filterDefs },
    search: "client",
  });

  const query = useCostCodesQuery({
    variables: {
      // Map our version number -> backend number[]
      filter: { version: layoutState.filter.version ? [layoutState.filter.version] : [1, 2] },
    },
  });
  // Adding "isOptionCatalogV2Enabled" because the drawer opens outside of the Feature Flag Context Provider.
  const isOptionCatalogV2Enabled = useFeatureFlag(FeatureFlagType.OptionsCatalogV2);
  const isReadonly = !useFeatureFlag(FeatureFlagType.CostCodesAdmin);

  const rowStyles = useMemo(
    () => createRowStyles(openInDrawer, rowLookup, isOptionCatalogV2Enabled, isReadonly),
    [openInDrawer, rowLookup, isOptionCatalogV2Enabled, isReadonly],
  );
  const columns = useMemo(() => createColumns(isReadonly), [isReadonly]);

  return (
    <PageHeaderLayout
      pageHeader={{ title: "Cost Codes", rightSlot: isReadonly ? undefined : <CreateNewCostCodeModal /> }}
    >
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        tableProps={{ sorting: { on: "client" }, query, createRows, rowStyles, columns, rowLookup }}
      />
    </PageHeaderLayout>
  );
}

type Row =
  | { kind: "header"; data: undefined }
  | { kind: "division"; id: string; data: AdminCostDivisionFragment }
  | { kind: "code"; id: string; data: AdminCostCodeFragment };

function createColumns(isReadonly: boolean): GridColumn<Row>[] {
  return [
    column<Row>({
      header: (data, { row }) => <CollapseToggle row={row} />,
      division: (data, { row }) => <CollapseToggle row={row} />,
      code: emptyCell,
      clientSideSort: false,
      w: "60px",
    }),
    numericColumn<Row>({ header: "Id", division: ({ id }) => id, code: ({ id }) => id, w: "60px" }),
    numericColumn<Row>({
      header: "Version",
      division: ({ version }) => version,
      code: ({ version }) => version,
      w: "80px",
    }),
    column<Row>({
      header: "Name",
      division: ({ name }) => name,
      code: ({ name }) => ({ content: name, indent: 1 }),
      w: "350px",
    }),
    numericColumn<Row>({
      header: "Number",
      division: ({ number }) => number,
      code: ({ number }) => ({ content: number, indent: 1 }),
      w: "120px",
    }),
    column<Row>({ header: "Stage", division: ({ stage }) => stage.name, code: emptyCell }),
    numericColumn<Row>({
      header: "Cost Classification",
      division: emptyCell,
      code: ({ costClassification }) => costClassification?.name,
    }),
    numericColumn<Row>({
      header: "Cost Sub Classification",
      division: emptyCell,
      code: ({ costSubClassification }) => costSubClassification?.name,
    }),
    numericColumn<Row>({
      header: "Invoice Category",
      division: emptyCell,
      code: ({ invoiceCategory }) => invoiceCategory?.name,
    }),
    numericColumn<Row>({
      header: "Intacct AP Account Id",
      division: emptyCell,
      code: ({ intacctApAccountId }) => intacctApAccountId,
    }),
    actionColumn<Row>({
      header: emptyCell,
      division: emptyCell,
      code: ({ id }) => (isReadonly ? emptyCell : <DeleteCostCodeButton id={id} />),
    }),
  ];
}

function createRowStyles(
  openInDrawer: ReturnType<typeof useSuperDrawer>["openInDrawer"],
  lookup: MutableRefObject<GridRowLookup<Row> | undefined>,
  isOptionCatalogV2Enabled: boolean,
  isReadonly: boolean,
): RowStyles<Row> {
  function openRow(row: GridDataRow<Row>): void {
    if (row.kind === "code") {
      const { prev, next } = lookup.current!.lookup(row)["code"];
      openInDrawer({
        onPrevClick: prev && (() => openRow(prev)),
        onNextClick: next && (() => openRow(next)),
        content: (
          <CostCodesSuperDrawerTabs
            costCode={row.data}
            isOptionCatalogV2Enabled={isOptionCatalogV2Enabled}
            isReadonly={isReadonly}
          />
        ),
      });
    }
  }
  return { code: { onClick: openRow } };
}

function createRows(data: CostCodesQuery | undefined): GridDataRow<Row>[] {
  const costDivisions = groupBy(data?.costCodes || [], (cc) => cc.division.id);

  return [
    simpleHeader,
    ...(Object.values(costDivisions).map((costCodes) => ({
      kind: "division" as const,
      id: costCodes[0].division.id,
      data: costCodes[0].division,
      children: costCodes.map((cc) => ({ kind: "code" as const, id: cc.id, data: cc })),
    })) || []),
  ];
}

function DeleteCostCodeButton({ id }: { id: string }) {
  const { triggerNotice } = useSnackbar();
  const [deleteCostCode] = useDeleteCostCodeMutation({
    update: (cache) => cache.evict({ id: cache.identify({ __typename: "CostCode", id }) }),
  });
  const onDelete = async () => {
    await deleteCostCode({ variables: { input: { id } } });
    triggerNotice({ message: "Cost code deleted", icon: "success" });
  };

  return <Button variant="secondary" label="Delete" onClick={onDelete} />;
}

type LocalCostCodeFilter = { version: number };
